import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VideocamIcon from '@mui/icons-material/Videocam';
import ListIcon from '@mui/icons-material/List';
import WorkIcon from '@mui/icons-material/Work';
import TableViewIcon from '@mui/icons-material/TableView';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import FolderIcon from '@mui/icons-material/Folder';

import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useAuth } from '../../contexts/AuthContext';

const Header: React.FC = () => {
  const location = useLocation();
  const { user, logout, login, isAuthenticated, promptSuppressed, renderGoogleButton } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  
  // Simplified environment: 'production' | 'staging' | 'dev'
  const envName = process.env.REACT_APP_ENVIRONMENT;
  const isDev = envName === 'dev';
  const isStaging = envName === 'staging';
  
  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  const navItems = [
    { path: '/upload', label: 'Upload', icon: <CloudUploadIcon /> },
    { path: '/productions', label: 'Productions', icon: <ListIcon /> },
    { path: '/ads', label: 'Ads Library', icon: <TableViewIcon /> },
    { path: '/licenses', label: 'Licenses', icon: <MusicNoteIcon /> },
    { path: '/organization', label: 'My Personal Space (Beta)', icon: <FolderIcon /> },
    { path: '/jobs', label: 'Jobs', icon: <WorkIcon /> },
  ].filter(Boolean) as { path: string; label: string; icon: React.ReactNode }[];

  return (
    <AppBar 
      position="static" 
      elevation={1}
      sx={{
        // Colors per environment
        backgroundColor: isDev ? '#6a1b9a' : (isStaging ? '#2e7d32' : 'primary.main'),
        '&.MuiAppBar-root': {
          backgroundColor: isDev ? '#6a1b9a' : (isStaging ? '#2e7d32' : undefined),
        }
      }}
    >
      <Toolbar>
        <Box
          component={Link}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            mr: 2,
            '&:hover': {
              opacity: 0.8,
            }
          }}
        >
          <VideocamIcon sx={{ mr: 1 }} />
          <Typography variant="h6" component="div">
            TheAdsFather
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          {(isDev || isStaging) && (
            <Chip
              label={isDev ? 'DEV' : 'STAGING'}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.7rem',
                height: '20px'
              }}
            />
          )}
        </Box>
        
        {isAuthenticated ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {navItems.map((item) => (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                color="inherit"
                startIcon={item.icon}
                variant={location.pathname === item.path ? 'outlined' : 'text'}
                sx={{
                  borderColor: location.pathname === item.path ? 'white' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {item.label}
              </Button>
            ))}

            {/* User Info Section */}
            <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label="@hellosimply"
                size="small"
                color="secondary"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />

              <Button
                color="inherit"
                onClick={handleUserMenuClick}
                startIcon={
                  user?.picture ? (
                    <Avatar
                      src={user.picture}
                      sx={{ width: 24, height: 24 }}
                    />
                  ) : (
                    <AccountCircleIcon />
                  )
                }
                sx={{
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {user?.name || 'User'}
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon sx={{ mr: 1 }} />
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        ) : (
          /* Public user section */
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body1" sx={{ color: 'white', mr: 2 }}>
              Viewing ad details
            </Typography>
            {promptSuppressed ? (
              <Box ref={renderGoogleButton} />
            ) : (
              <Button
                variant="outlined"
                color="inherit"
                onClick={login}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: 'white',
                  }
                }}
              >
                Sign In
              </Button>
            )}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header; 